import React, {
  useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { useIntl } from '@edx/frontend-platform/i18n';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';

import ConfigurableRegistrationForm from './components/ConfigurableRegistrationForm';
import RegistrationFailure from './components/RegistrationFailure';
import {
  backupRegistrationFormBegin,
  clearRegistrationBackendError,
  registerNewUser,
  setEmailSuggestionInStore,
  setUserPipelineDataLoaded,
} from './data/actions';
import {
  FORM_SUBMISSION_ERROR,
  TPA_AUTHENTICATION_FAILURE,
} from './data/constants';
import getBackendValidations from './data/selectors';
import {
  getAdditionalRegistrationFieldSteps,
  getVisibleRegistrationFieldDescriptions,
  isFormValid,
  prepareRegistrationPayload,
} from './data/utils';
import messages from './messages';
import { EmailField, NameField, UsernameField } from './RegistrationFields';
import {
  InstitutionLogistration,
  PasswordField,
  RedirectLogistration,
  ThirdPartyAuthAlert,
} from '../common-components';
import { getThirdPartyAuthContext as getRegistrationDataFromBackend } from '../common-components/data/actions';
import EnterpriseSSO from '../common-components/EnterpriseSSO';
import ThirdPartyAuth from '../common-components/ThirdPartyAuth';
import {
  COMPLETE_STATE, PENDING_STATE, REGISTER_PAGE,
} from '../data/constants';
import {
  getAllPossibleQueryParams, getTpaHint, getTpaProvider, isHostAvailableInQueryParams, setCookie,
} from '../data/utils';

/**
 * Main Registration Page component
 */
const RegistrationPage = (props) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [additionalFieldIndex, setAdditionalFieldIndex] = useState(0);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const registrationEmbedded = isHostAvailableInQueryParams();
  const platformName = getConfig().SITE_NAME;
  const flags = {
    showConfigurableEdxFields: getConfig().SHOW_CONFIGURABLE_EDX_FIELDS,
    showConfigurableRegistrationFields: getConfig().ENABLE_DYNAMIC_REGISTRATION_FIELDS,
    showMarketingEmailOptInCheckbox: getConfig().MARKETING_EMAILS_OPT_IN,
    autoGeneratedUsernameEnabled: getConfig().ENABLE_AUTO_GENERATED_USERNAME,
  };
  const {
    handleInstitutionLogin,
    institutionLogin,
  } = props;

  const backedUpFormData = useSelector(state => state.register.registrationFormData);
  const registrationError = useSelector(state => state.register.registrationError);
  const registrationErrorCode = registrationError?.errorCode;
  const registrationResult = useSelector(state => state.register.registrationResult);
  const shouldBackupState = useSelector(state => state.register.shouldBackupState);
  const userPipelineDataLoaded = useSelector(state => state.register.userPipelineDataLoaded);
  const submitState = useSelector(state => state.register.submitState);

  const fieldDescriptions = useSelector(state => state.commonComponents.fieldDescriptions);
  const optionalFields = useSelector(state => state.commonComponents.optionalFields);
  const thirdPartyAuthApiStatus = useSelector(state => state.commonComponents.thirdPartyAuthApiStatus);
  const autoSubmitRegForm = useSelector(state => state.commonComponents.thirdPartyAuthContext.autoSubmitRegForm);
  const thirdPartyAuthErrorMessage = useSelector(state => state.commonComponents.thirdPartyAuthContext.errorMessage);
  const finishAuthUrl = useSelector(state => state.commonComponents.thirdPartyAuthContext.finishAuthUrl);
  const currentProvider = useSelector(state => state.commonComponents.thirdPartyAuthContext.currentProvider);
  const providers = useSelector(state => state.commonComponents.thirdPartyAuthContext.providers);
  const secondaryProviders = useSelector(state => state.commonComponents.thirdPartyAuthContext.secondaryProviders);
  const pipelineUserDetails = useSelector(state => state.commonComponents.thirdPartyAuthContext.pipelineUserDetails);

  const backendValidations = useSelector(getBackendValidations);
  const queryParams = useMemo(() => getAllPossibleQueryParams(), []);
  const tpaHint = useMemo(() => getTpaHint(), []);

  const [formFields, setFormFields] = useState({ ...backedUpFormData.formFields });
  const [configurableFormFields, setConfigurableFormFields] = useState({ ...backedUpFormData.configurableFormFields });
  const [errors, setErrors] = useState({ ...backedUpFormData.errors });
  const [errorCode, setErrorCode] = useState({ type: '', count: 0 });
  const [formStartTime, setFormStartTime] = useState(null);
  // temporary error state for embedded experience because we don't want to show errors on blur
  const [temporaryErrors, setTemporaryErrors] = useState({ ...backedUpFormData.errors });

  const { cta, host } = queryParams;
  const buttonLabel = cta
    ? formatMessage(messages['create.account.cta.button'], { label: cta })
    : formatMessage(messages['create.account.for.free.button']);
  const visibleFieldDescriptions = getVisibleRegistrationFieldDescriptions(fieldDescriptions, flags);
  const additionalFieldSteps = getAdditionalRegistrationFieldSteps(fieldDescriptions, flags);
  const hasAdditionalFields = additionalFieldSteps.length > 0;
  const isLastAdditionalField = additionalFieldIndex >= additionalFieldSteps.length - 1;
  const activeAdditionalField = additionalFieldSteps[additionalFieldIndex];

  /**
   * Set the userPipelineDetails data in formFields for only first time
   */
  useEffect(() => {
    if (!userPipelineDataLoaded && thirdPartyAuthApiStatus === COMPLETE_STATE) {
      if (thirdPartyAuthErrorMessage) {
        setErrorCode(prevState => ({ type: TPA_AUTHENTICATION_FAILURE, count: prevState.count + 1 }));
      }
      if (pipelineUserDetails && Object.keys(pipelineUserDetails).length !== 0) {
        const { name = '', username = '', email = '' } = pipelineUserDetails;
        setFormFields(prevState => ({
          ...prevState, name, username, email,
        }));
        dispatch(setUserPipelineDataLoaded(true));
      }
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    thirdPartyAuthApiStatus,
    thirdPartyAuthErrorMessage,
    pipelineUserDetails,
    userPipelineDataLoaded,
  ]);

  useEffect(() => {
    if (!formStartTime) {
      sendPageEvent('login_and_registration', 'register');
      const payload = { ...queryParams, is_register_page: true };
      if (tpaHint) {
        payload.tpa_hint = tpaHint;
      }
      dispatch(getRegistrationDataFromBackend(payload));
      setFormStartTime(Date.now());
    }
  }, [dispatch, formStartTime, queryParams, tpaHint]);

  /**
   * Backup the registration form in redux when register page is toggled.
   */
  useEffect(() => {
    if (shouldBackupState) {
      dispatch(backupRegistrationFormBegin({
        ...backedUpFormData,
        configurableFormFields: { ...configurableFormFields },
        formFields: { ...formFields },
        errors: { ...errors },
      }));
    }
  }, [shouldBackupState, configurableFormFields, formFields, errors, dispatch, backedUpFormData]);

  useEffect(() => {
    if (backendValidations) {
      if (registrationEmbedded) {
        setTemporaryErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      } else {
        setErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      }
    }
  }, [backendValidations, registrationEmbedded]);

  useEffect(() => {
    if (registrationErrorCode) {
      setErrorCode(prevState => ({ type: registrationErrorCode, count: prevState.count + 1 }));
    }
  }, [registrationErrorCode]);

  useEffect(() => {
    if (registrationResult.success) {
      // This event is used by GTM
      sendTrackEvent('edx.bi.user.account.registered.client', {});

      // This is used by the "User Retention Rate Event" on GTM
      setCookie(getConfig().USER_RETENTION_COOKIE_NAME, true);
    }
  }, [registrationResult]);

  const handleOnChange = (event) => {
    const { name } = event.target;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (registrationError[name]) {
      dispatch(clearRegistrationBackendError(name));
    }
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    setFormFields(prevState => ({ ...prevState, [name]: value }));
  };

  const handleErrorChange = (fieldName, error) => {
    if (registrationEmbedded) {
      setTemporaryErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: error,
      }));
      if (error === '' && errors[fieldName] !== '') {
        setErrors(prevErrors => ({
          ...prevErrors,
          [fieldName]: error,
        }));
      }
    } else {
      setErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: error,
      }));
    }
  };

  const registerUser = () => {
    const totalRegistrationTime = (Date.now() - formStartTime) / 1000;
    let payload = { ...formFields };

    if (currentProvider) {
      delete payload.password;
      payload.social_auth_provider = currentProvider;
    }
    if (flags.autoGeneratedUsernameEnabled) {
      delete payload.username;
    }

    // Validating form data before submitting
    const { isValid, fieldErrors, emailSuggestion } = isFormValid(
      payload,
      registrationEmbedded ? temporaryErrors : errors,
      configurableFormFields,
      visibleFieldDescriptions,
      formatMessage,
    );
    setErrors({ ...fieldErrors });
    dispatch(setEmailSuggestionInStore(emailSuggestion));

    // returning if not valid
    if (!isValid) {
      setErrorCode(prevState => ({ type: FORM_SUBMISSION_ERROR, count: prevState.count + 1 }));
      return;
    }

    // Preparing payload for submission
    payload = prepareRegistrationPayload(
      payload,
      configurableFormFields,
      flags.showMarketingEmailOptInCheckbox,
      totalRegistrationTime,
      queryParams);

    // making register call
    dispatch(registerNewUser(payload));
  };

  const handleNextStep = (e) => {
    e.preventDefault();

    // Core fields validation for Step 1
    const step1Payload = { ...formFields };
    if (flags.autoGeneratedUsernameEnabled) {
      delete step1Payload.username;
    }
    if (currentProvider) {
      delete step1Payload.password;
    }

    // Dummy empty extra fields so it doesn't fail full validation prematurely
    const dummyConfigurable = {};
    if (configurableFormFields) {
      Object.keys(configurableFormFields).forEach(key => {
        dummyConfigurable[key] = configurableFormFields[key];
      });
    }

    const { fieldErrors } = isFormValid(
      step1Payload,
      registrationEmbedded ? temporaryErrors : errors,
      dummyConfigurable,
      visibleFieldDescriptions,
      formatMessage,
    );

    // If core fields have errors, block progression
    // We only care about step 1 errors here
    const coreErrors = {
      name: fieldErrors.name,
      email: fieldErrors.email,
      username: fieldErrors.username,
      password: fieldErrors.password,
    };

    const hasCoreErrors = !!coreErrors.name || !!coreErrors.email || !!coreErrors.username || !!coreErrors.password;

    setErrors(prev => ({ ...prev, ...coreErrors }));

    if (hasCoreErrors) {
      return;
    }

    if (hasAdditionalFields) {
      setAdditionalFieldIndex(0);
      setCurrentStep(2);
      return;
    }

    registerUser();
  };

  const validateActiveAdditionalField = () => {
    if (!activeAdditionalField) {
      return true;
    }

    const fieldErrors = {};
    const fieldValue = configurableFormFields[activeAdditionalField.name];
    const requiredFieldError = activeAdditionalField.fieldData?.error_message
      || formatMessage(messages['registration.empty.form.submission.error']);

    if (activeAdditionalField.type === 'country') {
      if (!configurableFormFields.country?.displayValue) {
        fieldErrors.country = formatMessage(messages['empty.country.field.error']);
      } else if (!configurableFormFields.country?.countryCode) {
        fieldErrors.country = formatMessage(messages['invalid.country.field.error']);
      }
    } else if (activeAdditionalField.type === 'field') {
      if (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim())) {
        fieldErrors[activeAdditionalField.name] = requiredFieldError;
      } else if (activeAdditionalField.name === 'confirm_email' && fieldValue !== formFields.email) {
        fieldErrors.confirm_email = formatMessage(messages['email.do.not.match']);
      }
    } else if (
      ['honor_code', 'terms_of_service'].includes(activeAdditionalField.type)
      && !fieldValue
    ) {
      fieldErrors[activeAdditionalField.name] = requiredFieldError;
    }

    if (Object.keys(fieldErrors).length) {
      if (registrationEmbedded) {
        setTemporaryErrors(prevErrors => ({ ...prevErrors, ...fieldErrors }));
      } else {
        setErrors(prevErrors => ({ ...prevErrors, ...fieldErrors }));
      }
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentStep === 2 && !isLastAdditionalField) {
      if (!validateActiveAdditionalField()) {
        return;
      }
      setAdditionalFieldIndex(prevIndex => prevIndex + 1);
      return;
    }
    registerUser();
  };

  useEffect(() => {
    if (autoSubmitRegForm && userPipelineDataLoaded) {
      registerUser();
    }
  }, [autoSubmitRegForm, userPipelineDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderForm = () => {
    if (institutionLogin) {
      return (
        <InstitutionLogistration
          secondaryProviders={secondaryProviders}
          headingTitle={formatMessage(messages['register.institution.login.page.title'])}
        />
      );
    }
    let registerButtonLabel = buttonLabel;
    if (currentStep === 1 && hasAdditionalFields) {
      registerButtonLabel = formatMessage(messages['registration.continue.button']);
    } else if (currentStep === 2 && !isLastAdditionalField) {
      registerButtonLabel = formatMessage(messages['registration.additional.info.next']);
    }

    return (
      <div className="w-full flex flex-col items-center py-6 sm:py-8 px-4 sm:px-6 lg:px-8 font-sans">
        <Helmet>
          <title>{formatMessage(messages['register.page.title'], { siteName: getConfig().SITE_NAME })}</title>
        </Helmet>
        <RedirectLogistration
          host={host}
          authenticatedUser={registrationResult.authenticatedUser}
          success={registrationResult.success}
          redirectUrl={registrationResult.redirectUrl}
          finishAuthUrl={finishAuthUrl}
          optionalFields={optionalFields}
          registrationEmbedded={registrationEmbedded}
          redirectToProgressiveProfilingPage={
            getConfig().ENABLE_PROGRESSIVE_PROFILING_ON_AUTHN && !!Object.keys(optionalFields.fields).length
          }
        />

        {autoSubmitRegForm && !errorCode.type ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <svg className="animate-spin h-10 w-10 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="mt-4 text-neutral-500 font-medium tracking-wide animate-pulse">
              {formatMessage(messages['registration.auto.submit.pending'])}
            </p>
          </div>
        ) : (
          <div
            className={classNames(
              'relative z-10 w-full',
              { 'auth-card max-w-[500px] mx-auto': !registrationEmbedded },
              { 'max-w-md mx-auto pt-4': registrationEmbedded },
            )}
          >
            {/* Logo and Header (only if not embedded) */}
            {!registrationEmbedded && (
              <div className="flex justify-center mb-4">
                <a href={getConfig().LMS_BASE_URL} className="flex items-center gap-2 group">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand group-hover:rotate-12 transition-transform duration-500 ease-out"><circle cx="12" cy="8" r="6" /><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" /></svg>
                  <span className="text-2xl font-bold text-neutral-900 tracking-tight">{getConfig().SITE_NAME}</span>
                </a>
              </div>
            )}

            <div className="mb-4">
              <ThirdPartyAuthAlert
                currentProvider={currentProvider}
                platformName={platformName}
                referrer={REGISTER_PAGE}
              />

              <RegistrationFailure
                errorCode={errorCode.type}
                failureCount={errorCode.count}
                context={{ provider: currentProvider, errorMessage: thirdPartyAuthErrorMessage }}
              />
            </div>

            <div className={classNames({ 'wuti-auth-card p-5 sm:p-6': !registrationEmbedded })}>
              {!registrationEmbedded && (
                <div className="text-center mb-4">
                  <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight mb-1">
                    {formatMessage(messages['register.page.heading'])}
                  </h1>
                  <p className="text-sm text-neutral-500">
                    {formatMessage(messages['register.sign.in.prompt'])}
                    {' '}
                    <a href={`${getConfig().LMS_BASE_URL}/login`} className="wuti-link">
                      {formatMessage(messages['register.sign.in.link'])}
                    </a>
                  </p>
                </div>
              )}

              <form id="registration-form" name="registration-form" onSubmit={(e) => { e.preventDefault(); if (currentStep === 1) { handleNextStep(e); } else { handleSubmit(e); } }} className="mt-4">

                {currentStep === 1 && (
                  <div className="animate-[fadeIn_0.3s_ease-out]">
                    <NameField
                      name="name"
                      value={formFields.name}
                      shouldFetchUsernameSuggestions={!formFields.username.trim()}
                      handleChange={handleOnChange}
                      handleErrorChange={handleErrorChange}
                      errorMessage={errors.name}
                      helpText={[formatMessage(messages['help.text.name'])]}
                      floatingLabel={formatMessage(messages['registration.fullname.label'])}
                      placeholder={formatMessage(messages['registration.fullname.placeholder'])}
                    />
                    <EmailField
                      name="email"
                      value={formFields.email}
                      confirmEmailValue={configurableFormFields?.confirm_email}
                      handleErrorChange={handleErrorChange}
                      handleChange={handleOnChange}
                      errorMessage={errors.email}
                      helpText={[formatMessage(messages['help.text.email'])]}
                      floatingLabel={formatMessage(messages['registration.email.label'])}
                      placeholder={formatMessage(messages['registration.email.placeholder'])}
                    />
                    {!flags.autoGeneratedUsernameEnabled && (
                      <UsernameField
                        name="username"
                        spellCheck="false"
                        value={formFields.username}
                        handleChange={handleOnChange}
                        handleErrorChange={handleErrorChange}
                        errorMessage={errors.username}
                        helpText={[formatMessage(messages['help.text.username.1']), formatMessage(messages['help.text.username.2'])]}
                        floatingLabel={formatMessage(messages['registration.username.label'])}
                        placeholder={formatMessage(messages['registration.username.placeholder'])}
                      />
                    )}
                    {!currentProvider && (
                      <PasswordField
                        name="password"
                        value={formFields.password}
                        handleChange={handleOnChange}
                        handleErrorChange={handleErrorChange}
                        errorMessage={errors.password}
                        floatingLabel={formatMessage(messages['registration.password.label'])}
                        placeholder="••••••••"
                      />
                    )}
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="animate-[fadeIn_0.3s_ease-out]">
                    <div className="mb-6">
                      <button
                        type="button"
                        onClick={() => {
                          if (additionalFieldIndex > 0) {
                            setAdditionalFieldIndex(prevIndex => prevIndex - 1);
                            return;
                          }
                          setCurrentStep(1);
                        }}
                        className="group flex items-center wuti-link mb-4 text-sm"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1 group-hover:-translate-x-1 transition-transform"><path d="m15 18-6-6 6-6" /></svg>
                        {formatMessage(messages['registration.back.button'])}
                      </button>
                      <h3 className="text-xl font-bold text-neutral-900">
                        {formatMessage(messages['registration.additional.info.heading'])}
                      </h3>
                      <p className="text-sm text-neutral-500 mt-1 mb-6">
                        {formatMessage(messages['registration.additional.info.description'])}
                      </p>
                    </div>

                    <ConfigurableRegistrationForm
                      email={formFields.email}
                      fieldErrors={errors}
                      formFields={configurableFormFields}
                      setFieldErrors={registrationEmbedded ? setTemporaryErrors : setErrors}
                      setFormFields={setConfigurableFormFields}
                      autoSubmitRegistrationForm={autoSubmitRegForm}
                      activeFieldIndex={additionalFieldIndex}
                      fieldDescriptions={visibleFieldDescriptions}
                      wizardMode
                    />
                  </div>
                )}

                <button
                  id="register-user"
                  name="register-user"
                  type="submit"
                  disabled={submitState === 'pending'}
                  className="wuti-btn-primary w-full mt-4"
                >
                  {submitState === 'pending' ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {formatMessage(messages['registration.submit.pending'])}
                    </span>
                  ) : registerButtonLabel}
                </button>

                {!registrationEmbedded && currentStep === 1 && (
                  <div className="mt-6">
                    <ThirdPartyAuth
                      currentProvider={currentProvider}
                      providers={providers}
                      secondaryProviders={secondaryProviders}
                      handleInstitutionLogin={handleInstitutionLogin}
                      thirdPartyAuthApiStatus={thirdPartyAuthApiStatus}
                    />
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (tpaHint) {
    if (thirdPartyAuthApiStatus === PENDING_STATE) {
      return <Skeleton height={36} />;
    }
    const { provider, skipHintedLogin } = getTpaProvider(tpaHint, providers, secondaryProviders);
    if (skipHintedLogin) {
      window.location.href = getConfig().LMS_BASE_URL + provider.registerUrl;
      return null;
    }
    return provider ? <EnterpriseSSO provider={provider} /> : renderForm();
  }
  return (
    renderForm()
  );
};

RegistrationPage.propTypes = {
  institutionLogin: PropTypes.bool,
  // Actions
  handleInstitutionLogin: PropTypes.func,
};

RegistrationPage.defaultProps = {
  handleInstitutionLogin: null,
  institutionLogin: false,
};

export default RegistrationPage;
